import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { AnimatedNumber } from "../components/AnimatedNumber";
import { WhatsAppLine } from "../components/WhatsAppLine";
import { IMAGES, MCC_PHOTOS } from "../data/images";
import { EASE } from "../utils/motion";
const GROUPS = [{
  id: "language",
  label: "Language & Literacy",
  image: MCC_PHOTOS.library,
  courses: ["English", "Ontario Secondary School Literacy Course (OSSLC)"]
}, {
  id: "science",
  label: "Science & Mathematics",
  image: IMAGES.lab,
  courses: ["Biology", "Physics", "Chemistry", "Data Management", "Advanced Functions", "Calculus and Vectors"]
}, {
  id: "business",
  label: "Business & Technology",
  image: IMAGES.ict,
  courses: ["International Business", "Business Leadership", "Financial Accounting", "Computer Science"]
}, {
  id: "future",
  label: "Society & Future",
  image: IMAGES.learning,
  courses: ["Career Planning", "Economics", "Political Science", "Nutrition and Health Practicum", "Community Leadership"]
}];
export function Curriculum() {
  const [active, setActive] = useState(GROUPS[1].id);
  const reduce = useReducedMotion();
  const group = GROUPS.find(g => g.id === active) ?? GROUPS[0];
  function select(id: string) {
    setActive(id);
    window.dispatchEvent(new CustomEvent("mcc:conversion", {
      detail: {
        eventName: "programme_explorer_interaction",
        group: id
      }
    }));
  }
  return <>
      <PageHero variant="type" eyebrow="CURRICULUM & COURSES" title="The University Pathway Program." description="One purposeful academic year, built around the subjects universities look for." breadcrumb="Curriculum & Courses" data-id="element-912" data-mp="85b226wmomz4" />

      <main data-id="element-913" data-mp="2suqroyk425n6">
        <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-9 md:py-32" data-id="element-914" data-mp="2nya1lytcph7g">
          <div className="grid border-y border-[#061f38] md:grid-cols-3" data-id="element-915" data-mp="2o0d0l8chekob">
            <Reveal className="border-b border-[#dedbd3] py-10 md:border-b-0 md:border-r md:pr-8" data-id="element-916" data-mp="2pfyykuira245">
              <p className="text-[clamp(5rem,12vw,10rem)] font-medium leading-none tracking-[-0.08em]" data-id="element-917" data-mp="2udhamts2kxgd"><AnimatedNumber value={10} data-id="element-918" data-mp="2gdy2i0z9dm42" /></p>
              <p className="mt-4 text-sm text-[#4f5963]" data-id="element-919" data-mp="2kg2wx1xj6mrr">month programme duration</p>
            </Reveal>
            <Reveal delay={0.1} className="border-b border-[#dedbd3] py-10 md:border-b-0 md:border-r md:px-8" data-id="element-920" data-mp="26oy0e8pgmaln">
              <p className="text-[clamp(5rem,12vw,10rem)] font-medium leading-none tracking-[-0.08em]" data-id="element-921" data-mp="2bfwm5nt7w0wa">6–<AnimatedNumber value={8} data-id="element-922" data-mp="1wqry3pzqr2g9" /></p>
              <p className="mt-4 text-sm text-[#4f5963]" data-id="element-923" data-mp="1ws36k344sydp">courses per student</p>
            </Reveal>
            <Reveal delay={0.2} className="py-10 md:pl-8" data-id="element-924" data-mp="21q4kk9mpa7ib">
              <p className="text-[clamp(5rem,12vw,10rem)] font-medium leading-none tracking-[-0.08em] text-[#bb1e35]" data-id="element-925" data-mp="1npaehxn4kimt"><AnimatedNumber value={110} data-id="element-926" data-mp="1sp6gxsg6c209" /></p>
              <p className="mt-4 text-sm text-[#4f5963]" data-id="element-927" data-mp="1sqzp5c6fquad">hours of instruction per credit</p>
            </Reveal>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-12 md:items-end" data-id="element-928" data-mp="1440fxzpcxrtk">
            <p className="text-2xl font-medium leading-snug tracking-[-0.03em] md:col-span-7 md:text-3xl" data-id="element-929" data-mp="1464sf61bnz5g">“A carefully curated learning experience for 21st-century careers.”</p>
            <p className="text-xs leading-5 text-[#6b7378] md:col-span-4 md:col-start-9" data-id="element-930" data-mp="1e0mxaeih60qu">Curriculum and course list as published by MCC, and subject to change. Confirm current courses with Admissions.</p>
          </div>
        </section>

        <section className="bg-[#061f38] py-24 text-white md:py-32" data-id="element-931" data-mp="21rso1opyedfx">
          <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-932" data-mp="21qzdsdrsd5t1">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-933" data-mp="1wsfqa8xjnlyk">
              <div data-id="element-934" data-mp="2arp637hu8e20">
                <p className="text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-935" data-mp="2at8mcy3hup3s">PROGRAMME EXPLORER</p>
                <h2 className="mt-5 text-4xl font-medium tracking-[-0.06em] md:text-6xl" data-id="element-936" data-mp="25uyrkololqip">What will my child study?</h2>
              </div>
              <p className="max-w-xs text-sm leading-6 text-white/60" data-id="element-937" data-mp="93b79u0u1dt3">Choose a subject area to see the courses listed in MCC’s current curriculum.</p>
            </div>

            <div className="mt-14 grid gap-10 lg:grid-cols-12" data-id="element-938" data-mp="dujecjeown33">
              <div role="tablist" aria-label="Subject areas" className="flex gap-2 overflow-x-auto pb-2 lg:col-span-4 lg:flex-col lg:gap-0 lg:overflow-visible lg:border-t lg:border-white/20 lg:pb-0" data-id="element-939" data-mp="hvuzocqj6m5h">
                {GROUPS.map((g, i) => {
                const selected = g.id === active;
                return <button key={g.id} role="tab" aria-selected={selected} onClick={() => select(g.id)} className={`relative shrink-0 whitespace-nowrap border px-4 py-3 text-left text-sm transition-colors duration-200 lg:border-0 lg:border-b lg:border-white/20 lg:px-0 lg:py-6 lg:text-2xl lg:tracking-[-0.03em] ${selected ? "border-white text-white" : "border-white/20 text-white/45 hover:text-white/80"}`} data-id="element-940" data-mp="3q9v612b1iv9s" data-mpi={i}>
                      <span className="mr-4 hidden text-xs text-[#f0b1b9] lg:inline" data-id="element-941" data-mp="1knfm3fir4pn6">0{i + 1}</span>
                      {g.label}
                      {selected && <motion.span layoutId="curriculum-tab" transition={{
                    duration: reduce ? 0 : 0.4,
                    ease: EASE
                  }} className="absolute bottom-[-1px] left-0 hidden h-px w-full bg-[#c5233d] lg:block" data-id="element-942" data-mp="1ay5us1vqfb04" />}
                    </button>;
              })}
              </div>

              <div className="grid gap-8 md:grid-cols-2 lg:col-span-8" data-id="element-943" data-mp="wymo7dikk7px">
                <AnimatePresence mode="wait" data-id="element-944" data-mp="wxbllh0v160e">
                  <motion.div key={group.id + "-img"} initial={{
                  opacity: 0,
                  clipPath: reduce ? "inset(0 0 0 0)" : "inset(0 0 100% 0)"
                }} animate={{
                  opacity: 1,
                  clipPath: "inset(0 0 0% 0)"
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: reduce ? 0.01 : 0.6,
                  ease: EASE
                }} className="relative aspect-[4/5] overflow-hidden" data-id="element-945" data-mp="rz09ezn3ejf0">
                    <img src={group.image} alt={group.label} className="absolute inset-0 h-full w-full object-cover" data-id="element-946" data-mp="2idaldrw8lafu" />
                  </motion.div>
                </AnimatePresence>
                <AnimatePresence mode="wait" data-id="element-947" data-mp="kgag0hkoez1c">
                  <motion.ul key={group.id} initial="hidden" animate="show" exit="hidden" variants={{
                  hidden: {},
                  show: {
                    transition: {
                      staggerChildren: reduce ? 0 : 0.05
                    }
                  }
                }} className="self-end border-t border-white/20" data-id="element-948" data-mp="ki3sidhz0dh9">
                    {group.courses.map(course => <motion.li key={course} variants={{
                    hidden: {
                      opacity: 0,
                      x: reduce ? 0 : -12
                    },
                    show: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: reduce ? 0.01 : 0.4,
                        ease: EASE
                      }
                    }
                  }} className="border-b border-white/20 py-4 text-lg tracking-[-0.02em]" data-id="element-949" data-mp="ygbi20cogcie">
                        {course}
                      </motion.li>)}
                  </motion.ul>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 py-28 md:px-9 md:py-36" data-id="element-950" data-mp="3f5h2vio3tat6">
          <WordReveal as="p" text="Every course blends real-world content, interactive multimedia, exercises and collaborative activities, building the academic skills needed to succeed." className="max-w-5xl text-3xl font-medium leading-[1.12] tracking-[-0.045em] md:text-5xl" data-id="element-951" data-mp="36mnhjo1lena2" />
          <Reveal delay={0.2} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8" data-id="element-952" data-mp="3bmrn4j0ypxli">
            <WhatsAppLine label="Ask about current course availability" context="the MCC curriculum and available courses" data-id="element-953" data-mp="2nwe41i0cv932" />
            <span className="text-xs text-[#6b7378]" data-id="element-954" data-mp="2iyvsdjuyefrd">Course availability varies by intake.</span>
          </Reveal>
        </section>
      </main>

      <CTABand title="Find the right course combination for your child." image={IMAGES.ict} data-id="element-955" data-mp="2xojw0rqlxqi9" />
    </>;
}